import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getCollectorToken } from "../../../helpers/collectorSession";
import SiteLayout, { SUPPORT_WHATSAPP } from "./SiteLayout";
import { Ripple } from "./SiteBits";
import { TAG_PRICES_INR, formatAsOf, formatInr, formatUsd, useUsdRate } from "./usdRate";
import collectorImg from "../assets/images/site/consumer.jpg";
import brocadeImg from "../assets/images/site/buyer-trust.jpg";
import stackImg from "../assets/images/site/sustainable-consumption.jpg";
import zariImg from "../assets/images/site/front-page-2-3.jpg";

const FEATURES: [string, string, string][] = [
  ["⌁", "Verify in one tap", "Tap the NFC tag or scan the QR label. See the maker, technique, loom and process — straight from the studio's record."],
  ["✓", "Owned, not just saved", "When a studio records your purchase against your phone number, the piece lands in your collection as owned."],
  ["↺", "Ownership history", "Gift it or pass it on and the record moves with it, so the piece always carries its line."],
];

const MORE_FEATURES: [string, string, string][] = [
  ["♡", "Care that fits the craft", "Wash, fold and storage guidance for each piece — silk, zari and cotton age differently."],
  ["+", "Add what you already own", "No tag? Add a photo and what you remember. Add a QR label or NFC tag whenever you like."],
  ["⇪", "Share on your terms", "Show a piece to family, a buyer or an appraiser — only the details you choose."],
];

const MOMENTS: [string, string, string][] = [
  ["◇", "Weddings & trousseau", "Record every bridal piece with its maker, so the family knows exactly what it holds."],
  ["↗", "Gifting", "Give a handwoven piece with a tag the recipient can tap — the gift explains itself."],
  ["⟳", "Heirlooms & inheritance", "Pass pieces down with their ownership history attached, not just a story."],
  ["₹", "Resale & valuation", "Show a buyer or appraiser the certificate instead of asking them to trust you."],
];

const CollectorsPage = () => {
  const { inrPerUsd, asOf, live } = useUsdRate();
  const [pieces, setPieces] = useState(20);
  const usd = (inr: number) => formatUsd(inr, inrPerUsd);
  const startTo = getCollectorToken() ? "/my-collection" : "/my-collection/login";
  const startLabel = getCollectorToken() ? "Open my collection" : "Start my collection — free";

  const faqs: [string, string][] = [
    ["Do I need to install an app?", "No. My Collection runs in your phone's browser. Tapping an NFC tag or scanning a QR label opens the certificate directly."],
    ["Is My Collection free?", `Yes. Scanning and saving tagged pieces is free, and so is adding every piece you already own, as many as you like. You only pay for the QR labels (${usd(TAG_PRICES_INR.qr)} · ${formatInr(TAG_PRICES_INR.qr)}) or NFC tags (${usd(TAG_PRICES_INR.nfc)} · ${formatInr(TAG_PRICES_INR.nfc)}) you choose to add.`],
    ["QR or NFC — which should I choose?", "QR is perfect for everyday pieces and gifts. NFC is harder to copy and locks to the piece once issued — choose it for bridal, heirloom and high-value work."],
    ["Who can see what I paid?", "Only you. Price and where you bought a piece never appear on any public card or shared link."],
    ["What if my piece was bought before tags existed?", "Add it with a photo and what you remember, then order a tag for it whenever you're ready."],
  ];

  return (
    <SiteLayout title="For collectors — HastKala Pramanak">
      <section className="c-hero">
        <div className="s-wrap c-hero__grid">
          <div>
            <span className="s-eyebrow">My Collection · for people who keep handmade</span>
            <h1>
              Know what you own.
              <br />
              <span className="s-grad">Prove it forever.</span>
            </h1>
            <p className="s-lead">
              Every saree, shawl and heirloom you keep — with its maker, its certificate and its story — in one private
              vault on your phone. Tap a tag to verify. Pass it on with its history intact.
            </p>
            <div className="s-ctas">
              <Link className="s-btn s-btn--primary" to={startTo}>{startLabel}</Link>
              <a className="s-btn s-btn--ghost" href="/home#verify">Verify a piece</a>
            </div>
            <div className="c-pricebar">
              <div><b>$0</b><span>My Collection</span></div>
              <div><b>{usd(TAG_PRICES_INR.qr)}</b><span>QR label per piece<small>{formatInr(TAG_PRICES_INR.qr)}</small></span></div>
              <div><b>{usd(TAG_PRICES_INR.nfc)}</b><span>NFC tag per piece<small>{formatInr(TAG_PRICES_INR.nfc)}</small></span></div>
            </div>
          </div>
          <div className="c-photo">
            <img src={collectorImg} alt="A woman wearing a handwoven Banarasi saree" />
            <div className="c-tap" aria-hidden="true">
              <Ripple />
              <small>NFC · VERIFIED</small>
              <p>Handwoven, Varanasi</p>
            </div>
            <div className="c-phone" aria-hidden="true">
              <div className="c-phone__scr">
                <div className="c-phone__top">My Collection<span>3 pieces</span></div>
                <div className="c-item"><img src={brocadeImg} alt="" /><div><b>Kadhua Banarasi</b><span>Bought 2026</span><br /><span className="c-tagl">NFC · verified</span></div></div>
                <div className="c-item"><img src={stackImg} alt="" /><div><b>Mother's wedding saree</b><span>Heirloom · 1994</span><br /><span className="c-tagl c-tagl--q">QR · added</span></div></div>
                <div className="c-item"><img src={zariImg} alt="" /><div><b>Zari border stole</b><span>Gift</span><br /><span className="c-tagl c-tagl--p">No tag yet</span></div></div>
                <div className="c-lock"><i>●</i>Price &amp; where bought — only you</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="c-night">
        <div className="s-wrap">
          <span className="s-eyebrow">Why it matters</span>
          <h2>A handwoven saree can take weeks on the loom. A copy takes hours. Most buyers can't tell them apart.</h2>
          <div className="c-pgrid">
            <div><i>01</i><b>Copies look the same</b><span>Powerloom and printed copies are sold as "handwoven" every day. Without proof, you pay handloom prices for machine work.</span></div>
            <div><i>02</i><b>Stories fade</b><span>Who wove it, which loom, what it cost, where you found it — receipts get lost and memories blur within a few years.</span></div>
            <div><i>03</i><b>Value disappears at hand-over</b><span>Gifting, passing to the next generation or reselling — a piece without its history is just fabric.</span></div>
          </div>
        </div>
      </section>

      <section className="s-section s-reveal">
        <div className="s-wrap">
          <div className="s-head">
            <div>
              <span className="s-eyebrow">What you get</span>
              <h2>A vault, a verifier and a family record — in your browser.</h2>
            </div>
            <p className="s-lead">No app to install. Log in with your phone and a one-time code; login and sign-up are the same step.</p>
          </div>
          <div className="c-feat">
            {FEATURES.map(([ic, title, body]) => (
              <div className="c-f" key={title}><span className="c-f__ic" aria-hidden="true">{ic}</span><h3>{title}</h3><p>{body}</p></div>
            ))}
            <div className="c-f c-f--big">
              <div>
                <span className="c-f__ic" aria-hidden="true">◐</span>
                <h3>Private by default</h3>
                <p>You decide what others see. What you paid and where you bought it never appear on a public card.</p>
                <div className="c-privacy">
                  <div><span>Maker &amp; technique</span><span className="c-vis">PUBLIC</span></div>
                  <div><span>Certificate &amp; tag</span><span className="c-vis">PUBLIC</span></div>
                  <div><span>Price paid</span><span className="c-hid">ONLY YOU</span></div>
                  <div><span>Where you bought it</span><span className="c-hid">ONLY YOU</span></div>
                </div>
              </div>
              <img src={stackImg} alt="Folded handwoven silk sarees" loading="lazy" />
            </div>
            {MORE_FEATURES.map(([ic, title, body]) => (
              <div className="c-f" key={title}><span className="c-f__ic" aria-hidden="true">{ic}</span><h3>{title}</h3><p>{body}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="s-section s-section--tight s-reveal">
        <div className="s-wrap">
          <div className="s-head"><div><span className="s-eyebrow">How you start</span><h2>Three minutes to your first piece.</h2></div></div>
          <div className="c-steps">
            <div className="c-step"><span className="s-mono">01</span><h3>Log in with your phone</h3><p>One-time code. New and returning collectors use the same step.</p></div>
            <div className="c-step"><span className="s-mono">02</span><h3>Tap, scan or photograph</h3><p>Tagged pieces bring their certificate. Untagged ones start from a photo.</p></div>
            <div className="c-step"><span className="s-mono">03</span><h3>Tag it for life</h3><p>Order a QR label or NFC tag for the pieces that matter most.</p></div>
          </div>
        </div>
      </section>

      <section className="s-section c-pricing" id="pricing">
        <div className="s-wrap">
          <div className="s-head">
            <div><span className="s-eyebrow">Pricing</span><h2>Free to keep. Cents to prove.</h2></div>
            <p className="s-lead">Your collection is free. Pay only for the tags you put on your pieces — once, for the life of the piece.</p>
          </div>
          <div className="c-pcards">
            <div className="c-pc">
              <span className="c-pc__k">My Collection</span>
              <h3>Your private vault</h3>
              <div className="c-amt"><b>$0</b><span>free plan</span></div>
              <div className="c-inr">no card needed</div>
              <ul>
                <li>Save every tagged piece you scan</li>
                <li>Add every piece you already own, no limit</li>
                <li>Ownership history</li>
                <li>Care guides</li>
                <li>Private price &amp; notes</li>
              </ul>
              <Link className="s-btn s-btn--ghost" to={startTo}>Start free</Link>
            </div>
            <div className="c-pc">
              <span className="c-pc__k">QR label</span>
              <h3>Printed label, sewn or tagged on</h3>
              <div className="c-amt"><b>{usd(TAG_PRICES_INR.qr)}</b><span>per piece</span></div>
              <div className="c-inr">{formatInr(TAG_PRICES_INR.qr)} · one-time</div>
              <ul>
                <li>Unique code bound to one piece</li>
                <li>Scans with any phone camera</li>
                <li>Links to its certificate &amp; story</li>
                <li>Good for everyday and gift pieces</li>
              </ul>
              <a className="s-btn s-btn--ghost" href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Order QR labels</a>
              <p className="c-compare">Less than a cup of chai — per piece.</p>
            </div>
            <div className="c-pc c-pc--hl">
              <span className="c-ribbon">Best for heirlooms</span>
              <span className="c-pc__k">NFC tag</span>
              <h3>Smart chip, tap to verify</h3>
              <div className="c-amt"><b>{usd(TAG_PRICES_INR.nfc)}</b><span>per piece</span></div>
              <div className="c-inr">{formatInr(TAG_PRICES_INR.nfc)} · one-time</div>
              <ul>
                <li>Tap with a phone — no camera, no app</li>
                <li>Locked to the piece once issued</li>
                <li>Hardest to copy or swap</li>
                <li>Made for bridal &amp; heirloom pieces</li>
              </ul>
              <a className="s-btn s-btn--primary" href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Order NFC tags</a>
              <p className="c-compare">Costs less than one dry-clean — protects the piece for life.</p>
            </div>
          </div>

          <div className="c-calc">
            <div>
              <h3>A {pieces}-saree collection, fully tagged</h3>
              <p>Slide to your collection size. Tags are a one-time cost — no subscription.</p>
              <input
                type="range"
                min={1}
                max={50}
                value={pieces}
                onChange={(e) => setPieces(Number(e.target.value))}
                aria-label="Number of pieces"
              />
              <div className="c-calc__ticks"><span>1</span><b>{pieces} pieces</b><span>50</span></div>
            </div>
            <div className="c-calc__out" aria-live="polite">
              <div><small>ALL QR</small><b>{usd(pieces * TAG_PRICES_INR.qr)}</b><span>{formatInr(pieces * TAG_PRICES_INR.qr)}</span></div>
              <div><small>ALL NFC</small><b>{usd(pieces * TAG_PRICES_INR.nfc)}</b><span>{formatInr(pieces * TAG_PRICES_INR.nfc)}</span></div>
            </div>
          </div>
          <p className="c-note">
            USD at {live ? "today's" : "a reference"} rate <span className="s-mono">₹{inrPerUsd.toFixed(2)} = $1</span>
            {live && asOf ? ` (${formatAsOf(asOf)})` : ""} · refreshed daily · billed in INR. Taxes and shipping extra.
          </p>
        </div>
      </section>

      <section className="s-section s-reveal">
        <div className="s-wrap">
          <div className="s-head"><div><span className="s-eyebrow">Made for the moments that matter</span><h2>Pieces worth keeping deserve proof.</h2></div></div>
          <div className="c-moments">
            <img src={brocadeImg} alt="Red and gold Banarasi brocade" loading="lazy" />
            <div className="c-mlist">
              {MOMENTS.map(([ic, title, body]) => (
                <div className="c-mi" key={title}><span className="c-mi__ic" aria-hidden="true">{ic}</span><div><b>{title}</b><span>{body}</span></div></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="s-section s-section--tight s-reveal">
        <div className="s-wrap c-faq">
          <div>
            <span className="s-eyebrow">Questions</span>
            <h2 style={{ marginTop: "var(--s2)" }}>Before you start</h2>
            <p className="s-lead" style={{ fontSize: 17, marginTop: "var(--s2)" }}>Still unsure? Our team answers on WhatsApp in Hindi and English.</p>
            <a className="s-btn s-btn--ghost" style={{ marginTop: "var(--s3)" }} href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Ask on WhatsApp</a>
          </div>
          <div>
            {faqs.map(([q, a]) => (
              <details className="c-qa" key={q}><summary>{q}</summary><p>{a}</p></details>
            ))}
          </div>
        </div>
      </section>

      <section className="s-final s-reveal">
        <div className="s-wrap">
          <span className="s-eyebrow">Start today</span>
          <h2>Your collection already has stories. Give them proof.</h2>
          <p className="s-lead">Free to start. Your phone number is all you need.</p>
          <div className="s-ctas">
            <Link className="s-btn s-btn--primary" to={startTo}>{startLabel}</Link>
            <a className="s-btn s-btn--ghost" href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Order tags</a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default CollectorsPage;
